import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './CustomPropertyColorSelect.css';

type CustomPropertyColorSelectProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** Mapa nombre del color → hex (para el círculo dentro de cada opción) */
  colors: Record<string, string>;
  placeholder: string;
};

export const CustomPropertyColorSelect = ({
  id,
  value,
  onChange,
  colors,
  placeholder,
}: CustomPropertyColorSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [listStyle, setListStyle] = useState<React.CSSProperties>({});

  const colorNames = Object.keys(colors);
  const selectedHex = value ? colors[value] : null;

  // Posicionar la lista debajo del trigger (viewport: fixed no usa scrollY)
  useEffect(() => {
    if (!isOpen || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setListStyle({
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
      minWidth: rect.width,
    });
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        listRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (name: string) => {
    onChange(name);
    setIsOpen(false);
  };

  const dropdownList = isOpen && (
    <ul
      ref={listRef}
      className="custom-color-select__list"
      role="listbox"
      style={listStyle}
      aria-labelledby={id}
    >
      <li
        role="option"
        aria-selected={!value}
        className="custom-color-select__option custom-color-select__option--placeholder"
        onClick={() => {
          onChange('');
          setIsOpen(false);
        }}
      >
        {placeholder}
      </li>
      {colorNames.map((name) => (
        <li
          key={name}
          role="option"
          aria-selected={value === name}
          className="custom-color-select__option"
          onClick={() => handleSelect(name)}
        >
          <span
            className="custom-color-select__circle"
            style={{ backgroundColor: colors[name] }}
          />
          <span>{name}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="custom-color-select container__input">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="custom-color-select__trigger custom-select"
        onClick={handleTriggerClick}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={placeholder}
      >
        {value ? (
          <span className="custom-color-select__trigger-content">
            <span
              className="custom-color-select__circle"
              style={{ backgroundColor: selectedHex ?? undefined }}
            />
            <span>{value}</span>
          </span>
        ) : (
          <span className="custom-color-select__placeholder">
            {placeholder}
          </span>
        )}
      </button>
      {createPortal(dropdownList, document.body)}
    </div>
  );
};
